import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'

import { GET, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

// Здоровье платформы — экран дежурного, а не клиента.
//
// Сверху светофор по нашим сервисам: сокет, очередь, база, синхронизация касс, storage.
// Снизу журнал синхронизаций по ВСЕМ точкам: сюда идут с вопросом «почему у клиента касса
// не синхронизировалась» — видно эндпоинт, код ответа, текст ошибки и тело запроса.
//
// Почти всё это уже писалось в базу и раньше: на 13.09.2026 в failed_jobs лежало 26 826
// падений рассылки из-за недоступного Reverb — просто никто на них не смотрел.
//
// Текст по-русски и без t(): админка не переводится (правило проекта).

const STATUS_CLASSES = {
	ok: 'text-success',
	warning: 'text-warning',
	error: 'text-danger',
}

const STATUS_TITLES = {
	ok: 'Норма',
	warning: 'Внимание',
	error: 'Проблема',
}

const PERIODS = [
	{ hours: 1, name: 'Час' },
	{ hours: 24, name: 'Сутки' },
	{ hours: 168, name: 'Неделя' },
]

function Index() {
	const [health, setHealth] = useState(null)
	const [logs, setLogs] = useState([])
	const [hours, setHours] = useState(24)
	const [search, setSearch] = useState('')
	const [onlyProblems, setOnlyProblems] = useState(true)

	const [showModal, setShowModal] = useState(false)
	const [selectedLog, setSelectedLog] = useState(null)

	async function getHealth() {
		const response = await GET('/admin/system-health', {}, { loader: true })
		if (httpOk(response)) setHealth(response.data)
	}

	async function getLogs(nextHours = hours, nextSearch = search, nextOnlyProblems = onlyProblems) {
		const response = await GET('/admin/system-health/sync-logs', {
			'hours': nextHours,
			'search': nextSearch,
			'only_problems': nextOnlyProblems,
			'size': 100,
		}, { loader: false })

		if (httpOk(response)) setLogs(response.data.data)
	}

	// Тело запроса кассы лежит отдельно: в списке оно не нужно, а весит много
	async function openLog(log) {
		setSelectedLog(log)
		setShowModal(true)

		const response = await GET(`/admin/system-health/sync-logs/${log.id}`, {}, { loader: false })
		if (httpOk(response)) setSelectedLog(response.data)
	}

	function handlePeriod(nextHours) {
		setHours(nextHours)
		getLogs(nextHours)
	}

	function handleOnlyProblems(bool) {
		setOnlyProblems(bool)
		getLogs(hours, search, bool)
	}

	// Значения метрик приходят вперемешку: числа, «да/нет» и даты последних событий.
	// Дату узнаём по виду и показываем по-человечески, остальное — как есть
	function formatValue(value) {
		if (value === null || value === undefined) return '—'

		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value)) {
			return formatDate(value, 'dd.MM.yyyy HH:mm')
		}

		return String(value)
	}

	function formatPayload(payload) {
		if (!payload) return ''

		try {
			return JSON.stringify(typeof payload === 'string' ? JSON.parse(payload) : payload, null, 2)
		} catch (error) {
			return String(payload)
		}
	}

	useEffect(() => {
		getHealth()
		getLogs()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="d-flex align-items-center mb-0 h-100">
					Здоровье платформы
					{health &&
						<span className={`ms-2 fs-6 ${STATUS_CLASSES[health.status]}`}>
							{STATUS_TITLES[health.status]}
						</span>
					}
				</h4>

				<button className="btn btn-outline-secondary" onClick={() => { getHealth(); getLogs() }}>
					<i className="uil uil-sync me-1"></i>
					Обновить
				</button>
			</div>

			<div className="row">
				{health?.metrics.map((metric) => (
					<div className="col-md-4 col-xl mb-3" key={metric.code}>
						<div className="card h-100">
							<div className="card-body p-3">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="fw-500">{metric.title}</span>
									<span className={STATUS_CLASSES[metric.status]}>●</span>
								</div>

								<div className={`fs-6 mb-2 ${STATUS_CLASSES[metric.status]}`}>
									{metric.description}
								</div>

								{Object.keys(metric.values).map((key) => (
									<div className="d-flex justify-content-between fs-6 text-muted" key={key}>
										<span>{key}</span>
										<span>{formatValue(metric.values[key])}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				))}
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
						<h6 className="mb-0 me-2">Ошибки синхронизации касс</h6>

						{PERIODS.map((period) => (
							<button key={period.hours}
								className={`btn btn-sm ${hours === period.hours ? 'btn-primary' : 'btn-outline-secondary'}`}
								onClick={() => handlePeriod(period.hours)}>
								{period.name}
							</button>
						))}

						<div className="form-check form-switch ms-2">
							<input type="checkbox" className="form-check-input" id="onlyProblems"
								checked={onlyProblems}
								onChange={(e) => handleOnlyProblems(e.target.checked)} />
							<label className="form-check-label" htmlFor="onlyProblems">Только ошибки</label>
						</div>

						<div className="position-relative ms-auto" style={{ 'width': '260px' }}>
							<i className="uil-search input-search-icon"></i>
							<input type="text" className="form-control form-control-sm"
								placeholder="Логин, ошибка, эндпоинт..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								onKeyUp={(e) => { if (e.keyCode === 13) getLogs(hours, e.target.value) }} />
						</div>
					</div>

					<table className="table table-bordered table-hover mb-0">
						<thead>
							<tr>
								<th style={{ 'width': '150px' }}>Время</th>
								<th>Точка</th>
								<th>Эндпоинт</th>
								<th style={{ 'width': '70px' }}>Код</th>
								<th>Ошибка</th>
								<th style={{ 'width': '110px' }}>Версия кассы</th>
							</tr>
						</thead>
						<tbody>
							{logs.map((log) => (
								<tr key={log.id} className="cursor-pointer" onClick={() => openLog(log)}>
									<td>{formatDate(log.created_at, 'dd.MM.yyyy HH:mm:ss')}</td>
									<td>
										{log.pos_name}
										<span className="ms-1 text-muted">{log.login}</span>
									</td>
									<td>{log.endpoint}</td>
									<td className={log.is_error ? 'text-danger' : ''}>{log.status_code}</td>
									<td className="text-danger">{log.error_message}</td>
									<td>{log.client_version}</td>
								</tr>
							))}

							{logs.length === 0 &&
								<tr>
									<td colSpan="6" className="text-center text-muted">Ошибок за период нет</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</div>

			{/* MODAL — одна запись журнала целиком, вместе с телом запроса от кассы */}
			<Modal show={showModal} animation={false} centered scrollable
				dialogClassName="update-modal-width" onHide={() => setShowModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedLog?.endpoint}
						<span className="ms-2 text-muted fs-6">{selectedLog?.pos_name} · {selectedLog?.login}</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ 'height': '500px' }}>
					{selectedLog &&
						<>
							<div className="row mb-3">
								<div className="col-md-3"><span className="text-muted">Время</span><div>{formatDate(selectedLog.created_at, 'dd.MM.yyyy HH:mm:ss')}</div></div>
								<div className="col-md-2"><span className="text-muted">Код ответа</span><div>{selectedLog.status_code}</div></div>
								<div className="col-md-2"><span className="text-muted">Длительность</span><div>{selectedLog.duration_ms} мс</div></div>
								<div className="col-md-2"><span className="text-muted">Позиций</span><div>{selectedLog.items_count}</div></div>
								<div className="col-md-3"><span className="text-muted">Устройство</span><div>{selectedLog.device}</div></div>
							</div>

							{selectedLog.error_message &&
								<div className="alert alert-danger">{selectedLog.error_message}</div>
							}

							{selectedLog.payload &&
								<pre className="bg-light p-2 fs-6" style={{ 'whiteSpace': 'pre-wrap' }}>
									{formatPayload(selectedLog.payload)}
								</pre>
							}
						</>
					}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
